import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy,Component, inject, Input, OnInit } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { isArray } from 'lodash-es';

import { AuthorizationContext, AuthorizationService } from 'app/shared/authentication';
import { LinkifiedTextComponent } from 'app/shared/components/linkified-text.component';
import {
    ArchivedOrderLine,
    ArchivedOrderLineLog,
    GlobalPermission,
    OrderLine,
    OrderLineApplicationInsightsLog,
    OrderLineJobApplicationInsightsException,
    OrderLineJobTraceTelemetry,
    OrderLineLogUnion
} from 'app/shared/graph';
import { FromNowPipe } from 'app/shared/utilities/from-now.pipe';

import { ApplicationInsightsLogDetailsComponent } from './application-insights-log-details.component';

@Component({
    selector: 'admin-rendering-job-log',
    imports: [
        DatePipe,
        FromNowPipe,
        MatDivider,
        MatIcon,
        MatIconButton,
        MatTooltip,
        ApplicationInsightsLogDetailsComponent,
        LinkifiedTextComponent,
        DecimalPipe
    ],
    templateUrl: './rendering-job-log.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './rendering-job-log.component.scss'
})
export class RenderingJobLogComponent implements OnInit {
    private readonly authService = inject(AuthorizationService);

    @Input() selectedLine!: ArchivedOrderLine | OrderLine;

    authContext: AuthorizationContext;
    @Input() logs!: OrderLineLogUnion[] | ArchivedOrderLineLog[];

    ngOnInit(): void {
        this.authService.authorizationContext().subscribe(authContext => this.authContext = authContext);
    }

    openApplicationInsightsForLog(url: string) {
        window.open(url);
    }

    canViewApplicationInsights() {
        return this.authContext.hasGlobalPermissions(GlobalPermission.ReadApplicationInsightsLogs);
    }

    extractInnerExceptionMessages(exception: OrderLineJobApplicationInsightsException): string[] {
        if (!exception.details) {
            return [];
        }
        let details = JSON.parse(exception.details);
        if (!isArray(details)) {
            return [];
        }
        return details.splice(1).map(e => e.message);
    }

    mergeApplicationInsightsLogs(log: OrderLineApplicationInsightsLog) {
        return log.exceptions.map(exception => {
            return {
                timestamp: new Date(exception.timestamp),
                itemId: exception.itemId,
                severityLevel: exception.severityLevel,
                message: exception.outerMessage,
                openQueryUrl: exception.openQueryUrl,
                exception: exception
            } as OrderLineApplicationInsightsLogUnion;
        }).concat(log.traces.map(trace => {
            return {
                timestamp: new Date(trace.timestamp),
                itemId: trace.itemId,
                severityLevel: trace.severityLevel,
                message: trace.message,
                openQueryUrl: trace.openQueryUrl,
                trace: trace
            } as OrderLineApplicationInsightsLogUnion;
        })).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    }
}

type OrderLineApplicationInsightsLogUnion = {
    timestamp: Date,
    itemId: string,
    severityLevel: string,
    message: string,
    openQueryUrl: string,
    exception: OrderLineJobApplicationInsightsException | undefined,
    trace: OrderLineJobTraceTelemetry | undefined;
}
